import React, { useState } from 'react';
import { User, Mail, Globe, Bell, Shield, Key, Save, Moon, Sun, Monitor, Sparkles, Table2, Zap, Wind, TrendingUp, BarChart3, ArrowLeftRight, Activity } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { useUserProfile } from '../hooks/useUserProfile';
const ATHENA_PRESETS = [{
  id: 'tables',
  label: 'Always use tables for comparisons',
  icon: Table2,
  default: true
}, {
  id: 'deltas',
  label: 'Show run deltas (Δ) in all forecasts',
  icon: TrendingUp,
  default: true
}, {
  id: 'divergence',
  label: 'Flag model divergence >15% as ⚠ signal',
  icon: Zap,
  default: true
}, {
  id: 'wind',
  label: 'Include wind capacity factors',
  icon: Wind,
  default: true
}, {
  id: 'spreads',
  label: 'Show DA vs ID price spreads',
  icon: BarChart3,
  default: true
}, {
  id: 'flows',
  label: 'Include cross-border flow analysis',
  icon: ArrowLeftRight,
  default: false
}, {
  id: 'balancing',
  label: 'Show balancing market signals',
  icon: Activity,
  default: false
}, {
  id: 'confidence',
  label: 'Tag confidence levels (HIGH/MED/LOW)',
  icon: Sparkles,
  default: true
}];
export function AccountSettingsView() {
  const {
    profile,
    updateProfile
  } = useUserProfile();
  const [timezone, setTimezone] = useState(profile.timezone);
  const [theme, setTheme] = useState('dark');
  const [units, setUnits] = useState(profile.units);
  const [athenaPresets, setAthenaPresets] = useState<Record<string, boolean>>(profile.athenaPresets);
  const [responseStyle, setResponseStyle] = useState(profile.responseStyle);
  const [defaultRegion, setDefaultRegion] = useState(profile.defaultRegion);
  const [defaultModels, setDefaultModels] = useState<string[]>(profile.defaultModels);
  const [firstName, setFirstName] = useState(profile.firstName);
  const [lastName, setLastName] = useState(profile.lastName);
  const [email, setEmail] = useState(profile.email);
  const togglePreset = (id: string) => {
    setAthenaPresets(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  const toggleModel = (model: string) => {
    setDefaultModels(prev => prev.includes(model) ? prev.filter(m => m !== model) : [...prev, model]);
  };
  const availableModels = ['EPT-2', 'EPT-2 Early', 'EPT-2 RR', 'EPT-2 HRRR', 'EPT-2e', 'EC AIFS', 'EC IFS', 'GFS', 'ICON Global', 'ICON-EU', 'Microsoft Aurora', 'EC ENS'];
  return <div className="jua-settings-page jua-page-bg p-8 max-w-5xl mx-auto space-y-8" data-id="element-2029">
      <div data-id="element-2030">
        <h1 className="text-2xl font-bold text-white mb-2" data-id="element-2031">Account Settings</h1>
        <p className="text-slate-400" data-id="element-2032">
          Manage your profile, preferences, and Athena AI configuration.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-2033">
        <div className="lg:col-span-1 space-y-2" data-id="element-2034">
          <button className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-[#EC4899]/10 text-[#EC4899] font-medium transition-colors" data-id="element-2035">
            <User size={18} data-id="element-2036" />
            Profile & Preferences
          </button>
          <button className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:text-white hover:bg-white/[0.04] transition-colors" data-id="element-2037">
            <Sparkles size={18} data-id="element-2038" />
            Athena AI Presets
          </button>
          <button className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:text-white hover:bg-white/[0.04] transition-colors" data-id="element-2039">
            <Bell size={18} data-id="element-2040" />
            Notifications
          </button>
          <button className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:text-white hover:bg-white/[0.04] transition-colors" data-id="element-2041">
            <Key size={18} data-id="element-2042" />
            API Keys
          </button>
          <button className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:text-white hover:bg-white/[0.04] transition-colors" data-id="element-2043">
            <Shield size={18} data-id="element-2044" />
            Security
          </button>
        </div>

        <div className="lg:col-span-2 space-y-6" data-id="element-2045">
          {/* Personal Information */}
          <Card className="p-6 border-slate-800 bg-[#111827]" data-id="element-2046">
            <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2" data-id="element-2047">
              <User size={20} className="text-[#EC4899]" data-id="element-2048" />
              Personal Information
            </h2>
            <div className="space-y-4" data-id="element-2049">
              <div className="flex items-center gap-6 mb-6" data-id="element-2050">
                <div className="h-20 w-20 rounded-full bg-[#EC4899] flex items-center justify-center text-white text-2xl font-bold shadow-lg shadow-pink-900/30" data-id="element-2051">
                  M
                </div>
                <div data-id="element-2052">
                  <Button variant="secondary" className="mb-2" data-id="element-2053">
                    Change Avatar
                  </Button>
                  <p className="text-xs text-slate-500" data-id="element-2054">
                    JPG, GIF or PNG. Max size of 800K
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2055">
                <div className="space-y-2" data-id="element-2056">
                  <label className="text-sm font-medium text-slate-300" data-id="element-2057">
                    First Name
                  </label>
                  <Input value={firstName} onChange={e => setFirstName(e.target.value)} data-id="element-2058" />
                </div>
                <div className="space-y-2" data-id="element-2059">
                  <label className="text-sm font-medium text-slate-300" data-id="element-2060">
                    Last Name
                  </label>
                  <Input value={lastName} onChange={e => setLastName(e.target.value)} data-id="element-2061" />
                </div>
              </div>
              <div className="space-y-2" data-id="element-2062">
                <label className="text-sm font-medium text-slate-300" data-id="element-2063">
                  Email Address
                </label>
                <Input value={email} onChange={e => setEmail(e.target.value)} icon={<Mail size={16} data-id="element-2065" />} data-id="element-2064" />
              </div>
            </div>
          </Card>

          {/* Athena AI Presets */}
          <Card className="p-6 border-slate-800 bg-[#111827]" data-id="element-2066">
            <h2 className="text-lg font-semibold text-white mb-2 flex items-center gap-2" data-id="element-2067">
              <Sparkles size={20} className="text-[#EC4899]" data-id="element-2068" />
              Athena AI Presets
            </h2>
            <p className="text-xs text-slate-500 mb-6" data-id="element-2069">
              Configure how Athena responds — optimized for energy trading speed
              and precision.
            </p>

            <div className="space-y-6" data-id="element-2070">
              {/* Response Style */}
              <div className="space-y-2" data-id="element-2071">
                <label className="text-sm font-medium text-slate-300" data-id="element-2072">
                  Response Style
                </label>
                <div className="flex rounded-lg overflow-hidden border border-slate-700 w-fit" data-id="element-2073">
                  <button onClick={() => setResponseStyle('concise')} className={`px-4 py-2 text-sm font-medium transition-colors ${responseStyle === 'concise' ? 'bg-[#EC4899] text-white' : 'bg-[#1E293B] text-slate-400 hover:text-white'}`} data-id="element-2074">
                    ⚡ Concise
                  </button>
                  <button onClick={() => setResponseStyle('tables-only')} className={`px-4 py-2 text-sm font-medium transition-colors ${responseStyle === 'tables-only' ? 'bg-[#EC4899] text-white' : 'bg-[#1E293B] text-slate-400 hover:text-white'}`} data-id="element-2075">
                    📊 Tables Only
                  </button>
                  <button onClick={() => setResponseStyle('detailed')} className={`px-4 py-2 text-sm font-medium transition-colors ${responseStyle === 'detailed' ? 'bg-[#EC4899] text-white' : 'bg-[#1E293B] text-slate-400 hover:text-white'}`} data-id="element-2076">
                    📝 Detailed
                  </button>
                </div>
              </div>

              {/* Default Region */}
              <div className="space-y-2" data-id="element-2077">
                <label className="text-sm font-medium text-slate-300" data-id="element-2078">
                  Default Trading Region
                </label>
                <div className="relative" data-id="element-2079">
                  <select value={defaultRegion} onChange={e => setDefaultRegion(e.target.value)} className="w-full h-10 px-3 pr-10 rounded-lg bg-[#1E293B] border border-slate-700 text-sm text-white focus:outline-none focus:border-[#EC4899] focus:ring-1 focus:ring-[#EC4899] appearance-none" data-id="element-2080">
                    <option value="central-europe" data-id="element-2081">
                      Central Europe (DE, FR, NL, AT, CH)
                    </option>
                    <option value="nordics" data-id="element-2082">Nordics (NO, SE, FI, DK)</option>
                    <option value="iberia" data-id="element-2083">Iberia (ES, PT)</option>
                    <option value="uk-ireland" data-id="element-2084">UK & Ireland</option>
                    <option value="italy" data-id="element-2085">Italy</option>
                    <option value="eastern-europe" data-id="element-2086">
                      Eastern Europe (PL, CZ, HU, RO)
                    </option>
                    <option value="all-europe" data-id="element-2087">All Europe</option>
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400" data-id="element-2088">
                    <Globe size={16} data-id="element-2089" />
                  </div>
                </div>
              </div>

              {/* Default Models */}
              <div className="space-y-2" data-id="element-2090">
                <label className="text-sm font-medium text-slate-300" data-id="element-2091">
                  Default Models for Comparison
                </label>
                <p className="text-[11px] text-slate-500" data-id="element-2092">
                  Athena will prioritize these models in delta and divergence
                  tables.
                </p>
                <div className="flex flex-wrap gap-2 mt-2" data-id="element-2093">
                  {availableModels.map(model => <button key={model} onClick={() => toggleModel(model)} className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${defaultModels.includes(model) ? 'border-[#EC4899] bg-[#EC4899]/15 text-[#EC4899]' : 'border-slate-700 bg-[#1E293B] text-slate-400 hover:border-slate-600 hover:text-slate-300'}`} data-id="element-2094">
                      {model}
                    </button>)}
                </div>
              </div>

              {/* Toggle Presets */}
              <div className="space-y-2" data-id="element-2095">
                <label className="text-sm font-medium text-slate-300" data-id="element-2096">
                  Auto-Include in Responses
                </label>
                <div className="space-y-1" data-id="element-2097">
                  {ATHENA_PRESETS.map(preset => <button key={preset.id} onClick={() => togglePreset(preset.id)} className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-white/[0.03] transition-colors" data-id="element-2098">
                      <div className="flex items-center gap-3" data-id="element-2099">
                        <preset.icon size={14} className={athenaPresets[preset.id] ? 'text-[#EC4899]' : 'text-slate-500'} data-id="element-2100" />
                        <span className={`text-sm ${athenaPresets[preset.id] ? 'text-white' : 'text-slate-400'}`} data-id="element-2101">
                          {preset.label}
                        </span>
                      </div>
                      <div className={`w-10 h-[22px] rounded-full p-0.5 transition-colors ${athenaPresets[preset.id] ? 'bg-[#EC4899]' : 'bg-slate-700'}`} data-id="element-2102">
                        <div className={`w-[18px] h-[18px] rounded-full bg-white shadow-sm transition-transform ${athenaPresets[preset.id] ? 'translate-x-[18px]' : 'translate-x-0'}`} data-id="element-2103" />
                      </div>
                    </button>)}
                </div>
              </div>
            </div>
          </Card>

          {/* Regional Preferences */}
          <Card className="p-6 border-slate-800 bg-[#111827]" data-id="element-2104">
            <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2" data-id="element-2105">
              <Globe size={20} className="text-[#EC4899]" data-id="element-2106" />
              Regional Preferences
            </h2>
            <div className="space-y-6" data-id="element-2107">
              <div className="space-y-2" data-id="element-2108">
                <label className="text-sm font-medium text-slate-300" data-id="element-2109">
                  Timezone
                </label>
                <div className="relative" data-id="element-2110">
                  <select value={timezone} onChange={e => setTimezone(e.target.value)} className="w-full h-10 px-3 pr-10 rounded-lg bg-[#1E293B] border border-slate-700 text-sm text-white focus:outline-none focus:border-[#EC4899] focus:ring-1 focus:ring-[#EC4899] appearance-none" data-id="element-2111">
                    <option value="UTC" data-id="element-2112">UTC</option>
                    <option value="Europe/London" data-id="element-2113">London (GMT/BST)</option>
                    <option value="Europe/Paris" data-id="element-2114">Paris (CET/CEST)</option>
                    <option value="Europe/Berlin" data-id="element-2115">Berlin (CET/CEST)</option>
                    <option value="Europe/Zurich" data-id="element-2116">Zurich (CET/CEST)</option>
                    <option value="Europe/Stockholm" data-id="element-2117">
                      Stockholm (CET/CEST)
                    </option>
                    <option value="Europe/Helsinki" data-id="element-2118">Helsinki (EET/EEST)</option>
                    <option value="America/New_York" data-id="element-2119">New York (EST/EDT)</option>
                    <option value="America/Chicago" data-id="element-2120">Chicago (CST/CDT)</option>
                    <option value="America/Los_Angeles" data-id="element-2121">
                      Los Angeles (PST/PDT)
                    </option>
                    <option value="Asia/Tokyo" data-id="element-2122">Tokyo (JST, UTC+9)</option>
                    <option value="Asia/Singapore" data-id="element-2123">
                      Singapore (SGT, UTC+8)
                    </option>
                    <option value="Australia/Sydney" data-id="element-2124">Sydney (AEST/AEDT)</option>
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400" data-id="element-2125">
                    <Globe size={16} data-id="element-2126" />
                  </div>
                </div>
              </div>

              <div className="space-y-2" data-id="element-2127">
                <label className="text-sm font-medium text-slate-300" data-id="element-2128">
                  Measurement Units
                </label>
                <div className="flex rounded-lg overflow-hidden border border-slate-700 w-fit" data-id="element-2129">
                  <button onClick={() => setUnits('metric')} className={`px-4 py-2 text-sm font-medium transition-colors ${units === 'metric' ? 'bg-[#EC4899] text-white' : 'bg-[#1E293B] text-slate-400 hover:text-white'}`} data-id="element-2130">
                    Metric (°C, m/s)
                  </button>
                  <button onClick={() => setUnits('imperial')} className={`px-4 py-2 text-sm font-medium transition-colors ${units === 'imperial' ? 'bg-[#EC4899] text-white' : 'bg-[#1E293B] text-slate-400 hover:text-white'}`} data-id="element-2131">
                    Imperial (°F, mph)
                  </button>
                </div>
              </div>

              <div className="space-y-2" data-id="element-2132">
                <label className="text-sm font-medium text-slate-300" data-id="element-2133">
                  Appearance
                </label>
                <div className="grid grid-cols-3 gap-3" data-id="element-2134">
                  <button onClick={() => setTheme('light')} className={`flex flex-col items-center gap-2 p-3 rounded-lg border transition-all ${theme === 'light' ? 'border-[#EC4899] bg-[#EC4899]/10 text-white' : 'border-slate-700 bg-[#1E293B] text-slate-400 hover:border-slate-600'}`} data-id="element-2135">
                    <Sun size={20} data-id="element-2136" />
                    <span className="text-xs" data-id="element-2137">Light</span>
                  </button>
                  <button onClick={() => setTheme('dark')} className={`flex flex-col items-center gap-2 p-3 rounded-lg border transition-all ${theme === 'dark' ? 'border-[#EC4899] bg-[#EC4899]/10 text-white' : 'border-slate-700 bg-[#1E293B] text-slate-400 hover:border-slate-600'}`} data-id="element-2138">
                    <Moon size={20} data-id="element-2139" />
                    <span className="text-xs" data-id="element-2140">Dark</span>
                  </button>
                  <button onClick={() => setTheme('system')} className={`flex flex-col items-center gap-2 p-3 rounded-lg border transition-all ${theme === 'system' ? 'border-[#EC4899] bg-[#EC4899]/10 text-white' : 'border-slate-700 bg-[#1E293B] text-slate-400 hover:border-slate-600'}`} data-id="element-2141">
                    <Monitor size={20} data-id="element-2142" />
                    <span className="text-xs" data-id="element-2143">System</span>
                  </button>
                </div>
              </div>
            </div>
          </Card>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-800" data-id="element-2144">
            <Button variant="secondary" data-id="element-2145">Cancel</Button>
            <Button className="flex items-center gap-2" onClick={() => {
            updateProfile({
              firstName,
              lastName,
              email,
              timezone,
              units,
              responseStyle,
              defaultRegion,
              defaultModels,
              athenaPresets
            });
          }} data-id="element-2146">
              <Save size={16} data-id="element-2147" />
              Save Changes
            </Button>
          </div>
        </div>
      </div>
    </div>;
}